import { useId, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";

// Tour T-27, T-30 (owner, 21 October 2026): "these tabs don't look like tabs". A real tab
// bar: a rule under the whole strip, the selected tab lifted onto it with a heavier
// rule, and the panel beneath. The selected tab is in the URL (?tab=), so a link, a
// reload and the back button all land on the same tab. Arrow keys move between tabs
// (roving tabindex), as the platform's tabs do.
export type TabSpec = { id: string; label: string; content: ReactNode };

export function Tabs({ tabs, label }: { tabs: TabSpec[]; label: string }) {
  const uid = useId();
  const [params, setParams] = useSearchParams();
  const wanted = params.get("tab");
  const active = tabs.find((t) => t.id === wanted) ?? tabs[0]!;

  function select(id: string) {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  }

  function onKey(e: React.KeyboardEvent, index: number) {
    const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (step === 0) return;
    e.preventDefault();
    const target = tabs[(index + step + tabs.length) % tabs.length]!;
    select(target.id);
    document.getElementById(`${uid}-tab-${target.id}`)?.focus();
  }

  return (
    <div className="tabs">
      <div className="shell-nav tab-strip" role="tablist" aria-label={label}>
        {tabs.map((tab, i) => (
          <button
            key={tab.id}
            id={`${uid}-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={tab.id === active.id}
            aria-controls={`${uid}-panel-${tab.id}`}
            tabIndex={tab.id === active.id ? 0 : -1}
            onClick={() => select(tab.id)}
            onKeyDown={(e) => onKey(e, i)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div id={`${uid}-panel-${active.id}`} role="tabpanel" aria-labelledby={`${uid}-tab-${active.id}`} className="tab-panel">
        {active.content}
      </div>
    </div>
  );
}
